<!-- (c) TwistedMinds, Gerry Uytdenhouwen - 5 november 2026 -->
<template>
    <div class="account-profile" :style="rootStyle">
    <form class="account-profile__profile" novalidate @submit.prevent="save">
        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-name">{{ $t('general.name') }}</label>
            <input id="account-profile-name" v-model="values.name" class="account-profile__input"
                   :class="{'has-error': hasError('name')}" type="text" autocomplete="name">
            <div v-if="hasError('name')" class="account-profile__error">{{ firstError('name') }}</div>
        </div>

        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-email">{{ $t('general.email') }}</label>
            <input id="account-profile-email" v-model="values.email" class="account-profile__input"
                   :class="{'has-error': hasError('email')}" type="email" autocomplete="email"
                   autocorrect="off" spellcheck="false">
            <div v-if="hasError('email')" class="account-profile__error">{{ firstError('email') }}</div>
            <div v-if="pendingEmail" class="account-profile__pending" role="status">
                <div class="account-profile__pending-text">
                    {{ $t('general.profile-email-pending', {newEmail: pendingEmail, email: storedEmail}) }}
                </div>
                <button type="button" class="account-profile__resend" :disabled="resending" @click="resend">
                    {{ $t('general.profile-email-resend') }}
                </button>
            </div>
        </div>

        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-phone">{{ $t('general.phone') }}</label>
            <input id="account-profile-phone" v-model="values.phone" class="account-profile__input"
                   :class="{'has-error': hasError('phone')}" type="tel" autocomplete="tel">
            <div v-if="hasError('phone')" class="account-profile__error">{{ firstError('phone') }}</div>
        </div>

        <div class="account-profile__actions">
            <button type="submit" class="account-profile__save" :disabled="saving || !changes.changed">
                {{ $t('general.profile-save') }}
            </button>
        </div>
    </form>

    <form class="account-profile__password" novalidate @submit.prevent="changePassword">
        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-password-current">{{ $t('general.password-current') }}</label>
            <input id="account-profile-password-current" v-model="password.current" class="account-profile__input"
                   :class="{'has-error': hasPasswordError('current_password')}" type="password"
                   autocomplete="current-password">
            <div v-if="hasPasswordError('current_password')" class="account-profile__error">{{ passwordError('current_password') }}</div>
        </div>

        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-password-new">{{ $t('general.new-password') }}</label>
            <input id="account-profile-password-new" v-model="password.next" class="account-profile__input"
                   :class="{'has-error': hasPasswordError('password')}" type="password" autocomplete="new-password">
            <div v-if="hasPasswordError('password')" class="account-profile__error">{{ passwordError('password') }}</div>
        </div>

        <div class="account-profile__field">
            <label class="account-profile__label" for="account-profile-password-repeat">{{ $t('general.password-repeat') }}</label>
            <input id="account-profile-password-repeat" v-model="password.repeat" class="account-profile__input"
                   :class="{'has-error': passwordState.mismatch}" type="password" autocomplete="new-password">
            <div v-if="passwordState.mismatch" class="account-profile__error">{{ $t('general.password-mismatch') }}</div>
        </div>

        <div class="account-profile__actions">
            <button type="submit" class="account-profile__save"
                    :disabled="changingPassword || !passwordState.complete || passwordState.mismatch">
                {{ $t('general.password-change') }}
            </button>
        </div>
    </form>
    </div>
</template>

<script>
import {getCustomerProfile, resendEmailChange, saveCustomerProfile, updateCustomerPassword} from "@/widget/api";
import {profileChanges} from "@/widget/profileForm";
import {passwordFormState} from "@/widget/passwordForm";
import {passwordErrorKeys, profileErrorKeys} from "@/widget/profileErrors";

export default {
    props: {
        customer: {type: Object, default: null},
        theme: {type: Object, default: () => ({})},
    },
    data() {
        const customer = this.customer || {};
        const stored = {
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
        };
        return {
            stored,
            values: {...stored},
            pendingEmail: null,
            errors: {},
            saving: false,
            resending: false,
            password: {current: '', next: '', repeat: ''},
            passwordErrors: {},
            changingPassword: false,
        };
    },
    computed: {
        rootStyle() {
            const radius = this.theme && this.theme.roundedCorners;
            return {
                '--account-profile-primary': (this.theme && this.theme.color) || '#1f2a2e',
                '--account-profile-radius': radius ? '14px' : '0',
                '--account-profile-button-radius': radius ? '10px' : '0',
                fontFamily: (this.theme && this.theme.fontFamily) || 'inherit',
            };
        },
        storedEmail() {
            return this.stored.email;
        },
        changes() {
            return profileChanges(this.stored, this.values);
        },
        passwordState() {
            return passwordFormState(this.password);
        },
    },
    methods: {
        // errors / passwordErrors hold a translation key per field: server texts are never shown.
        hasError(field) {
            return Boolean(this.errors[field]);
        },
        firstError(field) {
            return this.$t('general.' + this.errors[field]);
        },
        hasPasswordError(field) {
            return Boolean(this.passwordErrors[field]);
        },
        passwordError(field) {
            return this.$t('general.' + this.passwordErrors[field]);
        },
        changePassword() {
            if (this.changingPassword || !this.passwordState.complete || this.passwordState.mismatch) {
                return;
            }
            this.changingPassword = true;
            this.passwordErrors = {};
            updateCustomerPassword({
                currentPassword: this.password.current,
                password: this.password.next,
                passwordConfirmation: this.password.repeat,
            })
                .then(({ok, status, data}) => {
                    if (ok) {
                        this.password = {current: '', next: '', repeat: ''};
                        this.$emit('info', this.$t('general.password-changed'));
                    } else if (status === 422) {
                        const {fields, general} = passwordErrorKeys(data);
                        this.passwordErrors = fields;
                        if (general) {
                            this.$emit('error', this.$t('general.password-change-failed'));
                        }
                    } else if (status === 429) {
                        this.$emit('error', this.$t('general.profile-too-many'));
                    } else {
                        this.$emit('error', this.$t('general.password-change-failed'));
                    }
                })
                .catch(() => {
                    this.$emit('error', this.$t('general.password-change-failed'));
                })
                .finally(() => {
                    this.changingPassword = false;
                });
        },
        applyProfile(profile) {
            this.stored = {
                name: profile.name || '',
                email: profile.email || '',
                phone: profile.phone || '',
            };
            // The field shows the account address; a requested new address is shown in the info bar.
            this.values = {...this.stored};
            this.pendingEmail = profile.pending_email || null;
        },
        save() {
            if (this.saving || !this.changes.changed) {
                return;
            }
            this.saving = true;
            this.errors = {};
            saveCustomerProfile({
                name: this.values.name.trim(),
                phone: this.values.phone.trim(),
                email: this.values.email.trim(),
            })
                .then(({ok, status, data}) => {
                    if (ok) {
                        this.applyProfile(data);
                        this.$emit('saved', data);
                        this.$emit('info', this.$t('general.profile-saved'));
                    } else if (status === 422) {
                        const {fields, general} = profileErrorKeys(data);
                        this.errors = fields;
                        if (general) {
                            this.$emit('error', this.$t('general.profile-save-failed'));
                        }
                    } else if (status === 429) {
                        this.$emit('error', this.$t('general.profile-too-many'));
                    } else {
                        this.$emit('error', this.$t('general.profile-save-failed'));
                    }
                })
                .catch(() => {
                    this.$emit('error', this.$t('general.profile-save-failed'));
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        resend() {
            if (this.resending) {
                return;
            }
            this.resending = true;
            resendEmailChange()
                .then(({ok, status, data}) => {
                    if (ok) {
                        this.pendingEmail = data.pending_email || this.pendingEmail;
                        this.$emit('info', this.$t('general.profile-email-resent', {newEmail: this.pendingEmail}));
                    } else if (status === 404) {
                        // Nothing open any more (confirmed or replaced elsewhere): drop the bar.
                        this.pendingEmail = null;
                    } else if (status === 429) {
                        this.$emit('error', this.$t('general.profile-too-many'));
                    } else {
                        this.$emit('error', this.$t('general.profile-save-failed'));
                    }
                })
                .catch(() => {
                    this.$emit('error', this.$t('general.profile-save-failed'));
                })
                .finally(() => {
                    this.resending = false;
                });
        },
    },
    mounted() {
        getCustomerProfile()
            .then(({ok, data}) => {
                // Do not overwrite what the customer is already typing.
                if (ok && data && !this.changes.changed) {
                    this.applyProfile(data);
                } else if (ok && data) {
                    this.pendingEmail = data.pending_email || null;
                    this.stored = {name: data.name || '', email: data.email || '', phone: data.phone || ''};
                }
            })
            .catch(() => {
                // The values of the logged-in customer stay in the form.
            });
    },
};
</script>

<style scoped lang="scss">
// Fixed light palette and explicit colours/resets: nothing is inherited from the host page,
// whose element selectors (input, button, div) would otherwise win over inheritance.
.account-profile {
    color-scheme: light;
    background: #fff;
    color: #1f2a2e;
    font-size: 14px;
    line-height: normal;
    text-align: left;
    padding-top: 16px;

    &__password {
        padding-top: 16px;
    }

    &__field {
        margin-bottom: 14px;
    }

    &__label {
        display: block;
        margin: 0 0 4px;
        padding: 0;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.5;
        color: #6b7a80;
    }

    &__input {
        display: block;
        width: 100%;
        box-sizing: border-box;
        height: auto;
        min-height: 0;
        margin: 0;
        padding: 8px 10px;
        font-family: inherit;
        font-size: 14px;
        line-height: normal;
        color: #1f2a2e;
        background: #fff;
        border: 1px solid #cfd8dc;
        border-radius: var(--account-profile-button-radius);
        box-shadow: none;
        outline: none;
        appearance: none;
        -webkit-appearance: none;

        &:focus {
            border-color: var(--account-profile-primary);
        }

        &.has-error {
            border-color: red;
        }
    }

    &__error {
        margin-top: 4px;
        font-size: 12px;
        color: red;
    }

    &__pending {
        margin-top: 8px;
        padding: 8px 10px;
        background: #1976d2;
        border: 1px solid #0d47a1;
        border-radius: var(--account-profile-button-radius);
        color: #fff;
        font-size: 13px;
        line-height: 1.5;
    }

    &__pending-text {
        color: #fff;
        overflow-wrap: anywhere;
    }

    &__resend,
    &__save {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--account-profile-button-radius);
        font-family: inherit;
        font-weight: 600;
        font-size: 13px;
        line-height: normal;
        min-height: 0;
        margin: 0;
        text-transform: none;
        letter-spacing: normal;
        box-shadow: none;
        cursor: pointer;

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }

        &:focus-visible {
            outline: 2px solid var(--account-profile-primary);
            outline-offset: 2px;
        }
    }

    &__resend {
        margin-top: 8px;
        padding: 6px 12px;
        background: #fff;
        border: 1.5px solid var(--account-profile-primary);
        color: var(--account-profile-primary);
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 4px;
    }

    &__save {
        padding: 8px 18px;
        background: var(--account-profile-primary);
        border: 1.5px solid var(--account-profile-primary);
        color: #fff;
    }

    @media (max-width: 479px) {
        &__save {
            width: 100%;
        }
    }
}
</style>
